import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import Header from "../../components/header";
import Footer from "../../components/footer";
import Loader from "../../components/loader";
import { APP_NAVIGATION, LOCALE } from "../../utils/constants";
import { useNavigate, useParams } from "react-router-dom";
import PathHelper from "../../utils/path-helper";
import { readCookie } from "../../utils/helpers";
import { useTranslation } from "react-i18next";

function PreCancel({ getPlansConnect, funnelStep, updateUserPlanConnect }) {
  const { locale } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const currentLocale = locale ?? LOCALE.EN;
  const { countryCD, currencyCD, userGUID, email } = funnelStep || {};
  const [understood, setUnderstood] = useState(false);
  const [planData, setPlanData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [updatingPlan, setUpdatingPlan] = useState(false);
  const dashboardPath = PathHelper(APP_NAVIGATION.DASHBOARD_B, currentLocale);
  const cancelPlanFeedbackPath = PathHelper(
    APP_NAVIGATION.CANCEL_PLAN_FEEDBACK,
    currentLocale,
  );

  const settingsPath = PathHelper(APP_NAVIGATION.SETTINGS, currentLocale);
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  useEffect(() => {
    setLoading(true);
    getPlansConnect({
      planType: "_dunning",
      currencyCD,
      countryCD,
      device: "null",
      locale:
        currentLocale === LOCALE.EN ||
        currentLocale === LOCALE.ES ||
        currentLocale === LOCALE.JA
          ? currentLocale
          : LOCALE.EN,
    })
      .then((result) => {
        const topPlans = [...result]
          .sort((a, b) => b.unitPrice - a.unitPrice)
          .slice(0, 3);
        setPlanData(topPlans);
      })
      .catch((error) => {
        console.error("Failed to fetch plans:", error);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [getPlansConnect, currencyCD, countryCD, currentLocale]);

  const oneClickChoosePlan = (plan) => {
    const payload = {
      skuGuid: plan.id,
      price: plan.unitPrice,
      userGuid: userGUID,
      PaymentPortal: "INSIGHTFUL",
      Email: email,
      subscriptionId: readCookie("subscriptionId") || "",
      planId: plan.chargebeePlanId,
      isEndOfTerm: true,
      isIVRSource: false,
      locale: currentLocale,
    };
    setUpdatingPlan(true);
    updateUserPlanConnect(payload)
      .then((result) => {
        toast.success("Plan updated successfully!");
        navigate(`${settingsPath.next}?steps=billing&planUpdated=true`);
      })
      .catch((error) => {
        console.error("Failed to update plan:", error);
      })
      .finally(() => {
        setUpdatingPlan(false);
      });
  };
  return (
    <>
      <Header />
      <div className="w-full min-h-screen bg-white font-sans">
        {/* Headline */}
        <section className="px-6 sm:px-12 lg:px-20 pt-14 pb-10">
          <h1 className="text-slate-700 font-extrabold leading-tight text-3xl sm:text-4xl md:text-[48px] mb-6">
            {t("pre_cancel.sure_cancel")}
          </h1>
          <h2
            className="text-slate-700 font-bold text-xl sm:text-2xl"
            dangerouslySetInnerHTML={{
              __html: t("pre_cancel.keep_access"),
            }}
          />
        </section>

        {/* Plans */}
        <section className="bg-slate-50 border-t border-slate-200 px-6 sm:px-12 lg:px-20 py-16">
          <div className="max-w-6xl mx-auto min-h-[300px] flex items-center justify-center relative">
            <Loader isLoading={loading || updatingPlan} />
            {!loading && (
              <div
                className={`grid grid-cols-1 md:grid-cols-3 gap-8 w-full ${updatingPlan ? "opacity-50 pointer-events-none" : ""}`}
              >
                {planData?.map((plan, index) => (
                  <div
                    key={plan.id}
                    className="bg-white border border-slate-200 rounded-md p-8 flex flex-col items-center text-center"
                  >
                    <div className="text-orange-600 font-extrabold text-2xl mb-5">
                      {plan.currencySymbol}
                      {plan.unitPrice}
                      <span className="font-extrabold">
                        {index === 0
                          ? t("pre_cancel.users_5")
                          : index === 1
                            ? t("pre_cancel.users_3")
                            : t("pre_cancel.user_1")}
                      </span>
                    </div>

                    <p
                      className="text-slate-700 text-sm leading-relaxed mb-7 grow"
                      dangerouslySetInnerHTML={{ __html: plan.description }}
                    />

                    <button
                      className={`w-full border-2 border-orange-600 font-extrabold text-sm tracking-wide py-3.5 rounded-full cursor-pointer ${
                        plan.skuSubCategory === "M000195"
                          ? "bg-orange-600 text-white"
                          : "bg-white text-orange-600 ring-2 ring-offset-2 ring-orange-600"
                      }`}
                      onClick={() => oneClickChoosePlan(plan)}
                    >
                      {t("pre_cancel.choose")}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>

        {/* Still want to cancel */}
        <section className="px-6 sm:px-12 lg:px-20 py-14 w-full">
          <div className="max-w-2xl mx-auto">
            <h3 className="text-slate-700 font-extrabold text-xl mb-6">
              {t("pre_cancel.still_cancel")}
            </h3>

            <button
              type="button"
              className="w-full py-4 rounded-full bg-orange-600 hover:bg-orange-700 text-white font-extrabold text-base tracking-wide mb-4"
              onClick={() => navigate(dashboardPath.next)}
            >
              {t("pre_cancel.no_cancel")}
            </button>

            <button
              type="button"
              disabled={!understood}
              className={
                understood
                  ? "w-full py-4 rounded-full border border-slate-400 text-slate-700 font-extrabold text-base tracking-wide mb-5 hover:bg-slate-50 cursor-pointer ring-2 ring-offset-2 ring-gray-400"
                  : "w-full py-4 rounded-full border border-slate-300 text-slate-400 font-extrabold text-base tracking-wide mb-5 cursor-not-allowed ring-2 ring-offset-2 ring-gray-400"
              }
              onClick={() => navigate(cancelPlanFeedbackPath.next)}
            >
              {t("pre_cancel.yes_cancel")}
            </button>

            <label className="flex items-start gap-3 text-slate-600 text-base leading-relaxed cursor-pointer">
              <input
                type="checkbox"
                checked={understood}
                onChange={(e) => setUnderstood(e.target.checked)}
                className="mt-1 w-4 h-4 rounded border-slate-400 text-orange-600 focus:ring-orange-500"
              />
              <span>{t("pre_cancel.access_lose")}</span>
            </label>

            {!understood && (
              <p className="text-slate-500 text-sm mt-2 ml-7">
                {t("pre_cancel.checkbox")}
              </p>
            )}
          </div>
        </section>
      </div>
      <Footer />
    </>
  );
}

export default PreCancel;
